import { useState } from 'react'

import styled from 'styled-components'

import { Alert, Box, Button } from 'app/components/design-system-components'
import { Text } from 'app/components/design-system-components/typography'
import theme from 'app/match/applicationReportPDF/assets/theme'

const UnstyledLi = styled.li`
  display: list-item !important;
`

const UnstyledUl = styled.ul`
  padding-inline-start: 40px !important;
`

interface QldApplyFormInfoProps {
  expandable?: boolean
}

const QldApplyFormInfo = ({ expandable = false }: QldApplyFormInfoProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const toggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <Alert
      showLeftIcon={false}
      variant="blueWithBg"
      mt={7}
      mr={7}
      ml={7}
      height="100%"
    >
      <Box color="deepBlue">
        <Text as="h3" color="deepBlue" mb={5}>
          Queensland Form 22
        </Text>

        <Text fontSize={theme.fontSizes.pRegular14} color="deepBlue">
          Click to download an alternative{' '}
          <a
            href="https://www.rta.qld.gov.au/sites/default/files/2025-03/Form-22-Rental-application.pdf"
            target="_blank"
            rel="noreferrer"
          >
            Form 22
          </a>{' '}
          as pdf or read more information{' '}
          <a
            href="https://www.rta.qld.gov.au/forms-resources/rental-law-changes/application-process"
            target="_blank"
            rel="noreferrer"
          >
            about your rights and responsibilities
          </a>
          .
        </Text>

        {expandable && !isExpanded && (
          <Button
            variant="linkBlueUnderlinedWithHoverBold"
            onClick={toggleExpand}
          >
            Show more
          </Button>
        )}

        {(!expandable || isExpanded) && (
          <>
            <Text
              fontSize={theme.fontSizes.pRegular14}
              color="deepBlue"
              fontWeight="bold"
            >
              Important information
            </Text>
            <ol>
              <UnstyledLi>
                <Text
                  as="span"
                  fontSize={theme.fontSizes.pRegular14}
                  color="deepBlue"
                >
                  Application form: Property managers and owners must use a
                  standardised tenancy application form which complies with the
                  Residential Tenancies and Rooming Accommodation Act 2008 (the
                  Act) and the Residential Tenancies and Rooming Accommodation
                  Regulation 2009 (the Regulation).
                </Text>
              </UnstyledLi>
              <UnstyledLi>
                <Text
                  as="span"
                  fontSize={theme.fontSizes.pRegular14}
                  color="deepBlue"
                >
                  Exemptions: Relevant lessors, as defined under section 57B(7)
                  of the Act, are not required to use this standardised
                  application form.
                </Text>
              </UnstyledLi>
              <UnstyledLi>
                <Box fontSize={theme.fontSizes.pRegular14} color="deepBlue">
                  Ways to submit applications: Applicants must be given at least
                  2 different ways to submit their application, one of which
                  must not be a restricted way. Restricted ways are:
                  <UnstyledUl>
                    <UnstyledLi>
                      <Text
                        as="span"
                        fontSize={theme.fontSizes.pRegular14}
                        color="deepBlue"
                      >
                        where an applicant is required to provide their personal
                        information through an online platform to someone who is
                        not the property manager or owner, but who is collecting
                        the information on behalf of the property manager or
                        owner, and/or
                      </Text>
                    </UnstyledLi>
                    <UnstyledLi>
                      <Text
                        as="span"
                        fontSize={theme.fontSizes.pRegular14}
                        color="deepBlue"
                      >
                        a method that incurs a cost to the applicant such as an
                        application fee or the cost to conduct a background
                        check.
                      </Text>
                    </UnstyledLi>
                  </UnstyledUl>
                </Box>
              </UnstyledLi>
              <UnstyledLi>
                <Text
                  as="span"
                  fontSize={theme.fontSizes.pRegular14}
                  color="deepBlue"
                >
                  Request for information from applicants: Applicants can only
                  be asked for specific details, including proof of identity,
                  financial ability to pay rent, documents assessing their
                  suitability and references. Applicants cannot be asked to
                  provide information such as details about legal actions they
                  have taken, including previous tenancy disputes or matters
                  considered by the tribunal, history of rental bond claims,
                  breach notices given by and to the applicant, and statements
                  of credit accounts or bank accounts detailing transactions.
                </Text>
              </UnstyledLi>
              <UnstyledLi>
                <Text
                  as="span"
                  fontSize={theme.fontSizes.pRegular14}
                  color="deepBlue"
                >
                  The information provided must be used solely to assess an
                  applicant’s suitability as a tenant.
                </Text>
              </UnstyledLi>
              <UnstyledLi>
                <Text
                  as="span"
                  fontSize={theme.fontSizes.pRegular14}
                  color="deepBlue"
                >
                  An applicant’s personal information must be stored securely
                  and only used for the application process.
                </Text>
              </UnstyledLi>
              <UnstyledLi>
                <Text
                  as="span"
                  fontSize={theme.fontSizes.pRegular14}
                  color="deepBlue"
                >
                  An applicant should ensure that they keep a copy of their
                  application form for their records.
                </Text>
              </UnstyledLi>
              <UnstyledLi>
                <Text
                  as="span"
                  fontSize={theme.fontSizes.pRegular14}
                  color="deepBlue"
                >
                  If an applicant does not have the requested documentation,
                  they should discuss with the property manager or owner what
                  other documentation may be suitable.
                </Text>
              </UnstyledLi>
            </ol>
            <Text
              fontSize={theme.fontSizes.pRegular14}
              color="deepBlue"
              fontWeight="bold"
              mt={5}
            >
              Discrimination in accommodation
            </Text>

            <Text fontSize={theme.fontSizes.pRegular14} color="deepBlue">
              If an applicant believes they are unlawfully being discriminated
              against, they should contact the Queensland Human Rights
              Commission. The Commission handles complaints of discrimination,
              including those based on race, gender, age, disability,
              relationship status, sexuality, and other protected attributes
              under the Queensland Anti-Discrimination Act 1991. The Commission
              can provide guidance on how to file a complaint and assist in
              resolving issues related to unlawful discrimination.
            </Text>

            <Text
              fontSize={theme.fontSizes.pRegular14}
              color="deepBlue"
              fontWeight="bold"
            >
              Assessment of an application
            </Text>
            <Text fontSize={theme.fontSizes.pRegular14} color="deepBlue">
              A property manager or owner will assess the suitability of an
              applicant based on the information provided, including checking
              tenancy databases identified in Item 15. If an applicant is listed
              on a tenancy database, they will be informed of the listing
              details. For more information visit the RTA’s website. An
              applicant should ensure that they complete the application in full
              with true and correct information. A property manager or owner is
              not required to provide reasons to an applicant on why their
              application is unsuccessful.
            </Text>

            <Text
              fontSize={theme.fontSizes.pRegular14}
              color="deepBlue"
              fontWeight="bold"
            >
              Help or further information
            </Text>
            <Text fontSize={theme.fontSizes.pRegular14} color="deepBlue">
              For further information, visit the Residential Tenancies Authority
              (RTA) website at{' '}
              <a
                href="https://www.rta.qld.gov.au/"
                target="_blank"
                rel="noreferrer"
              >
                rta.qld.gov.au
              </a>{' '}
              or call the RTA’s Contact Centre on 1300 366 311.
            </Text>
          </>
        )}

        {expandable && isExpanded && (
          <Button
            variant="linkBlueUnderlinedWithHoverBold"
            onClick={toggleExpand}
          >
            Show less
          </Button>
        )}
      </Box>
    </Alert>
  )
}

export default QldApplyFormInfo
